import { Box, Input, Stack, Typography } from '@mui/material';
import { collection, doc, getDoc, onSnapshot, query, where } from 'firebase/firestore';
import React, { useEffect, useState } from 'react';
import { store } from '../../config/firebase';
import { liveGameDocs } from '../../helpers/games';
import GameByStatusCards from '../ui/gameStatusCards/gameByStatusCards';
import BSCircularProgress from '../ui/progress/circularProgress';

const EmbeddedBracketGames = ({ event: eventId }) => {
  const [loading, setLoading] = useState(false);
  const [games, setGames] = useState([]);
  const [filterInput, setFilterInput] = useState('');
  const [filteredGames, setFilteredGames] = useState([]);

  useEffect(() => {
    if (!eventId) {
      setGames([]);
      setFilteredGames([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setGames([]);

    const collectionRef = collection(store, 'games');
    const eventRef = doc(store, 'events', eventId);
    const q = query(
      collectionRef,
      where('event', 'in', [eventId, eventRef]),
      where('bracket', '!=', null)
    );

    const unsubscribe = onSnapshot(
      q,
      async (querySnapshot) => {
        const gamesData = await Promise.all(
          liveGameDocs(querySnapshot).map(async (docSnap) => {
            const gameData = { id: docSnap.id, ...docSnap.data() };

            if (gameData.venue && typeof gameData.venue === 'string') {
              try {
                const venueDoc = await getDoc(doc(store, 'venues', gameData.venue));
                if (venueDoc.exists()) {
                  gameData.venue = { id: venueDoc.id, ...venueDoc.data() };
                }
              } catch (e) {
                // Leave venue as-is if lookup fails.
              }
            }

            return gameData;
          })
        );

        const bracketGames = gamesData.sort((a, b) => {
          const aKey = `${a.green || ''}${a.rink || ''}`;
          const bKey = `${b.green || ''}${b.rink || ''}`;
          return aKey.localeCompare(bKey, undefined, { numeric: true });
        });

        setGames(bracketGames);
        setFilteredGames(bracketGames);
        setLoading(false);
      },
      () => {
        setGames([]);
        setFilteredGames([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [eventId]);

  useEffect(() => {
    if (filterInput === '') {
      setFilteredGames(games);
      return;
    }

    setFilteredGames(
      games.filter((game) => {
        const teamA = game.teams?.teamA?.players?.join(' ').toLowerCase() || '';
        const teamB = game.teams?.teamB?.players?.join(' ').toLowerCase() || '';
        return (
          teamA.includes(filterInput.toLowerCase()) || teamB.includes(filterInput.toLowerCase())
        );
      })
    );
  }, [filterInput, games]);

  return (
    <Stack gap={2}>
      <Input
        name="filter"
        label="Filter"
        placeholder="Filter by Surname"
        sx={{ textAlignLast: 'center', textAlign: 'center' }}
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
      />

      <Box display={'flex'} flexDirection={'row'} p={1} flexWrap={'wrap'} gap={2}>
        {loading ? (
          <BSCircularProgress caption={'Loading Games'} />
        ) : filteredGames.length !== 0 ? (
          filteredGames.map((game) => (
            <GameByStatusCards key={game.id} game={game} actions={false} showSectionWinners />
          ))
        ) : (
          <Box>
            <Typography variant="caption">There are currently no bracket games.</Typography>
          </Box>
        )}
      </Box>
    </Stack>
  );
};

export default EmbeddedBracketGames;
